import { useState, useEffect, useCallback } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import PeopleIcon from '@mui/icons-material/People';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SendIcon from '@mui/icons-material/Send';
import BlockIcon from '@mui/icons-material/Block';
import { LineChart } from '@mui/x-charts/LineChart';
import StatCard from '../components/StatCard';
import { getGlobalStats, StatsPeriod, GlobalStats } from '../api/stats';

const PERIOD_LABELS: Record<StatsPeriod, string> = {
    all: 'All Time',
    today: 'Today',
    week: 'This Week',
    month: 'This Month',
    lweek: 'Last Week',
    lmonth: 'Last Month',
    between: 'Custom Range',
};

const PAGE_SIZE = 25;

/**
 * Timeline x-axis label. For 'today' the API returns hourly buckets
 * ('YYYY-MM-DD HH', UTC) — display just the hour; other periods return
 * day buckets ('YYYY-MM-DD') rendered as-is.
 */
function timelineLabel(date: string, period: StatsPeriod): string {
    return period === 'today' ? `${date.slice(11)}:00` : date;
}

export default function StatisticsReportingPage() {
    const theme = useTheme();
    const [period, setPeriod] = useState<StatsPeriod>('all');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [data, setData] = useState<GlobalStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [badgePage, setBadgePage] = useState(1);

    const load = useCallback(async () => {
        // For a custom range, wait until both ends are set.
        if (period === 'between' && (!from || !to)) return;
        setLoading(true);
        setError('');
        const res = await getGlobalStats(period, from || undefined, to || undefined);
        setLoading(false);
        if (res.success && res.data) {
            setData(res.data);
            setBadgePage(1);
        } else {
            setError(res.message || 'Failed to load statistics.');
        }
    }, [period, from, to]);

    useEffect(() => { void load(); }, [load]);

    const totals = data?.totals;
    const timeline = data?.timeline ?? [];
    const byBadge = data?.byBadge ?? [];
    const pageCount = Math.ceil(byBadge.length / PAGE_SIZE);
    const pageRows = byBadge.slice((badgePage - 1) * PAGE_SIZE, badgePage * PAGE_SIZE);

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
                <Typography variant="h4" fontWeight={700}>Reports &amp; Statistics</Typography>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    {period === 'between' && (
                        <>
                            <TextField
                                type="date" size="small" label="From" InputLabelProps={{ shrink: true }}
                                value={from} onChange={e => setFrom(e.target.value)}
                            />
                            <TextField
                                type="date" size="small" label="To" InputLabelProps={{ shrink: true }}
                                value={to} onChange={e => setTo(e.target.value)}
                            />
                        </>
                    )}
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Period</InputLabel>
                        <Select value={period} label="Period" onChange={e => setPeriod(e.target.value as StatsPeriod)}>
                            {(Object.keys(PERIOD_LABELS) as StatsPeriod[])
                                .map(p => <MenuItem key={p} value={p}>{PERIOD_LABELS[p]}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {/* Summary cards */}
            <Grid container spacing={3} mb={4}>
                <Grid item xs={6} md={3}>
                    <StatCard loading={loading} value={totals?.activeUsers ?? 0} label="Active Users"
                        icon={<PeopleIcon color="primary" sx={{ fontSize: 32 }} />} />
                </Grid>
                <Grid item xs={6} md={3}>
                    <StatCard loading={loading} value={totals?.activeBadges ?? 0} label="Active Badges"
                        icon={<WorkspacePremiumIcon color="primary" sx={{ fontSize: 32 }} />} />
                </Grid>
                <Grid item xs={6} md={3}>
                    <StatCard loading={loading} value={totals?.issued ?? 0} label="Issued (period)"
                        color={theme.palette.success.main}
                        icon={<SendIcon sx={{ fontSize: 32, color: 'success.main' }} />} />
                </Grid>
                <Grid item xs={6} md={3}>
                    <StatCard loading={loading} value={totals?.revoked ?? 0} label="Revoked (period)"
                        color={theme.palette.error.main}
                        icon={<BlockIcon sx={{ fontSize: 32, color: 'error.main' }} />} />
                </Grid>
            </Grid>

            {/* Timeline chart */}
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight={600} mb={1}>Issued vs Revoked Over Time</Typography>
                    {loading ? (
                        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
                    ) : timeline.length === 0 ? (
                        <Box textAlign="center" py={6}>
                            <Typography color="text.secondary">No activity in this period.</Typography>
                        </Box>
                    ) : (
                        <LineChart
                            height={300}
                            xAxis={[{ scaleType: 'point', data: timeline.map(t => timelineLabel(t.date, period)) }]}
                            series={[
                                { data: timeline.map(t => t.issued), label: 'Issued', color: theme.palette.success.main },
                                { data: timeline.map(t => t.revoked), label: 'Revoked', color: theme.palette.error.main },
                            ]}
                        />
                    )}
                </CardContent>
            </Card>

            {/* Per-badge breakdown */}
            <Card>
                <CardContent>
                    <Typography variant="h6" fontWeight={600} mb={2}>By Badge</Typography>
                    {loading ? (
                        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
                    ) : byBadge.length === 0 ? (
                        <Box textAlign="center" py={6}>
                            <Typography color="text.secondary">No badges to report.</Typography>
                        </Box>
                    ) : (
                        <>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Badge</TableCell>
                                        <TableCell>Issuer</TableCell>
                                        <TableCell align="right">Issued</TableCell>
                                        <TableCell align="right">Revoked</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pageRows.map(b => (
                                        <TableRow key={b.badgeID}>
                                            <TableCell>{b.badgeName}</TableCell>
                                            <TableCell>{b.issuerName || '—'}</TableCell>
                                            <TableCell align="right">{b.issued.toLocaleString()}</TableCell>
                                            <TableCell align="right">{b.revoked.toLocaleString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {pageCount > 1 && (
                                <Box display="flex" justifyContent="center" mt={3}>
                                    <Pagination count={pageCount} page={badgePage} onChange={(_, p) => setBadgePage(p)} />
                                </Box>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </Container>
    );
}
